import { authService } from './authService';
// The ATS integration endpoints moved to the Go core API (services/data-pipeline
// cmd/api) in slice A4. Every method in this file calls /api/integrations*, and
// all fourteen of those endpoints are served there now, so the whole service
// repoints — hence the alias rather than a per-method change.
//
// The Python copies at API_BASE_URL stay live and untouched for rollback, as in
// A1 and A3; falling back means rebuilding the bundle, because REACT_APP_* is
// baked in at build time.
import { CORE_API_URL as API_URL, LOGIN_URL } from '../config';

/**
 * Helper function to handle 401 (Unauthorized) responses by redirecting to login
 * This handles token expiration gracefully
 * Note: This service is only used in the app domain, so we always redirect to the public login site
 */
const handleUnauthorized = () => {
  // Clear session storage
  sessionStorage.clear();
  
  // Redirect to public login site (signalcue.ai/login)
  // The app domain (app.signalcue.ai) redirects to the public domain for login
  window.location.href = `${LOGIN_URL}/login`;
};

/**
 * Helper function to check response for 401 and handle it
 * @param {Response} response - Fetch response object
 * @throws {Error} If response is 401, redirects to login and throws a specific error
 */
const checkUnauthorized = (response) => {
  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('Token has expired. Redirecting to login...');
  }
};

const getAuthHeaders = () => {
  const token = authService.requireToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export const integrationService = {
  /**
   * Get list of all integrations with their status
   * @returns {Promise<Array>} List of integrations
   */
  async getIntegrations() {
    const response = await fetch(`${API_URL}/api/integrations`, {
      headers: getAuthHeaders()
    });

    checkUnauthorized(response);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get integrations');
    }

    return response.json();
  },

  /**
   * Get configuration for a specific integration type
   * @param {string} type - Integration type (ismartrecruit, bullhorn, recruitcrm)
   * @returns {Promise<Object>} Integration configuration
   */
  async getIntegrationConfig(type) {
    const response = await fetch(`${API_URL}/api/integrations/${type}`, {
      headers: getAuthHeaders()
    });

    checkUnauthorized(response);

    if (!response.ok) {
      if (response.status === 404) {
        return null; // Integration not configured
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get integration config');
    }

    return response.json();
  },

  /**
   * Save integration configuration
   * @param {string} type - Integration type
   * @param {Object} config - Configuration object
   * @returns {Promise<Object>} Updated configuration
   */
  async saveIntegrationConfig(type, config) {
    const response = await fetch(`${API_URL}/api/integrations/${type}/configure`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(config)
    });

    checkUnauthorized(response);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to save integration config');
    }

    return response.json();
  },

  /**
   * Test connection to ATS
   * @param {string} type - Integration type
   * @param {Object} credentials - Credentials object
   * @returns {Promise<Object>} Test result
   */
  async testConnection(type, credentials) {
    const response = await fetch(`${API_URL}/api/integrations/${type}/test-connection`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ credentials })
    });

    checkUnauthorized(response);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Connection test failed');
    }

    return response.json();
  },

  /**
   * Manually trigger a sync
   * @param {string} type - Integration type
   * @returns {Promise<Object>} Sync run details
   */
  async triggerSync(type) {
    const response = await fetch(`${API_URL}/api/integrations/${type}/sync`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    checkUnauthorized(response);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to trigger sync');
    }

    return response.json();
  },

  /**
   * Get sync run logs/history
   * @param {string} type - Integration type
   * @param {number} limit - Number of logs to return (default: 50)
   * @returns {Promise<Array>} List of sync runs
   */
  async getSyncLogs(type, limit = 50) {
    const url = `${API_URL}/api/integrations/${type}/logs?limit=${limit}`;
    const headers = getAuthHeaders();
    
    try {
      // First, handle OPTIONS preflight if needed
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include',
        mode: 'cors'
      });

      checkUnauthorized(response);

      if (!response.ok) {
        // Try to get error details
        let errorMessage = `Failed to get sync logs (HTTP ${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (parseError) {
          // If not JSON, try text
          try {
            const text = await response.text();
            if (text) {
              errorMessage = text;
            }
          } catch (textError) {
            errorMessage = `${response.status} ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      // Parse JSON response
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        return Array.isArray(data) ? data : [];
      } else {
        // Non-JSON response - return empty array
        const text = await response.text();
        if (text.trim()) {
          console.warn('Unexpected non-JSON response from logs endpoint:', text.substring(0, 100));
        }
        return [];
      }
    } catch (err) {
      // Enhanced error handling
      if (err.name === 'TypeError' && (err.message.includes('Failed to fetch') || err.message.includes('NetworkError'))) {
        console.error('Network request failed:', {
          url: url,
          error: err.message,
          name: err.name
        });
        throw new Error(`Unable to connect to server. Please check your internet connection and try again.`);
      }
      // Re-throw other errors as-is
      throw err;
    }
  },

  /**
   * Get sample data from a snapshot
   * @param {string} type - Integration type
   * @param {string} syncRunId - Sync run ID
   * @param {string} objectType - Object type (candidates, companies, etc.)
   * @param {number} sampleSize - Number of sample records (default: 5)
   * @returns {Promise<Object>} Sample data with records and metadata
   */
  async getSnapshotSample(type, syncRunId, objectType, sampleSize = 5) {
    try {
      const response = await fetch(
        `${API_URL}/api/integrations/${type}/snapshots/${syncRunId}/sample/${objectType}?sample_size=${sampleSize}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include'
        }
      );

      checkUnauthorized(response);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
        throw new Error(error.detail || `Failed to get sample data (${response.status})`);
      }

      return response.json();
    } catch (err) {
      console.error('Failed to get sample data:', err);
      throw err;
    }
  },

  /**
   * Get list of data snapshots
   * @param {string} type - Integration type
   * @param {number} limit - Number of snapshots to return (default: 50)
   * @returns {Promise<Array>} List of snapshots
   */
  async getSnapshots(type, limit = 50) {
    const response = await fetch(`${API_URL}/api/integrations/${type}/snapshots?limit=${limit}`, {
      headers: getAuthHeaders()
    });

    checkUnauthorized(response);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get snapshots');
    }

    return response.json();
  },

  /**
   * Enable an integration
   * @param {string} type - Integration type
   * @returns {Promise<Object>} Updated configuration
   */
  async enableIntegration(type) {
    const response = await fetch(`${API_URL}/api/integrations/${type}/enable`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    checkUnauthorized(response);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to enable integration');
    }

    return response.json();
  },

  /**
   * Disable an integration
   * @param {string} type - Integration type
   * @returns {Promise<Object>} Updated configuration
   */
  async disableIntegration(type) {
    const response = await fetch(`${API_URL}/api/integrations/${type}/disable`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    checkUnauthorized(response);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to disable integration');
    }

    return response.json();
  },

  /**
   * Get active (pending or running) sync runs for an integration
   * @param {string} type - Integration type
   * @returns {Promise<Array>} List of active sync runs
   */
  async getActiveSyncs(type) {
    const response = await fetch(`${API_URL}/api/integrations/${type}/active-syncs`, {
      headers: getAuthHeaders()
    });

    checkUnauthorized(response);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get active syncs');
    }

    return response.json();
  },

  /**
   * Cancel an active sync run
   * @param {string} type - Integration type
   * @param {string} syncRunId - Sync run ID to cancel
   * @returns {Promise<Object>} Cancellation result
   */
  async cancelSync(type, syncRunId) {
    const response = await fetch(`${API_URL}/api/integrations/${type}/sync/${syncRunId}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    checkUnauthorized(response);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to cancel sync');
    }

    return response.json();
  }
};
